import { Controller } from 'react-hook-form';

import { useAppTranslations } from '@treneo/shared/i18n';
import { Chip, MultiSelect, Tooltip } from '@treneo/shared/ui';

import { translateErrorMessage } from '../constants/form-constants';
import { useAppFormContext } from '../hooks';

type Option = {
  id: string;
  label: string;
  description?: string;
};

type Props = {
  name: string;
  label: string;
  options: Option[];
  required?: boolean;
  fullWidth?: boolean;
};

export const MultiSelectField = ({
  name,
  label,
  options,
  required = false,
  fullWidth = true
}: Readonly<Props>) => {
  const { control } = useAppFormContext();
  const { t } = useAppTranslations();

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required: required ? t('FieldIsRequired') : false }}
      render={({ field: { onChange, value = [] }, fieldState: { error } }) => {
        const selectedOptions = options.filter(option => value.includes(option.id));
        const rawMessage = error ? translateErrorMessage(error) : '';
        let helperTextTranslated = '';
        if (error && rawMessage) {
          const translated = t(rawMessage);
          const hasTranslation = translated !== rawMessage;
          helperTextTranslated = hasTranslation ? translated : rawMessage;
        }

        return (
          <MultiSelect
            options={options}
            value={selectedOptions}
            onChange={newValue => onChange(newValue.map(option => option.id))}
            getOptionLabel={option => option.label}
            label={label}
            required={required}
            fullWidth={fullWidth}
            error={!!error}
            helperText={helperTextTranslated}
            renderValue={(value, getItemProps) =>
              value.map((option, index) => {
                const itemProps = getItemProps({ index });
                const maxLength = 20;
                const isLongLabel = option.label.length > maxLength;
                const truncatedLabel = isLongLabel
                  ? `${option.label.substring(0, maxLength)}...`
                  : option.label;

                return (
                  <Tooltip key={option.id} title={isLongLabel ? option.label : ''} arrow>
                    <Chip
                      {...itemProps}
                      variant='outlined'
                      label={truncatedLabel}
                      sx={{
                        height: '22px',
                        maxWidth: '120px',
                        fontSize: '0.75rem',
                        '& .MuiChip-label': {
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          padding: '0 6px',
                          lineHeight: '20px'
                        },
                        '& .MuiChip-deleteIcon': {
                          fontSize: '14px',
                          height: '14px',
                          width: '14px'
                        }
                      }}
                    />
                  </Tooltip>
                );
              })
            }
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '56px',
                padding: '6px 14px',
                '& .MuiAutocomplete-input': {
                  height: '44px',
                  padding: '0 !important',
                  margin: '0 !important'
                },
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'nowrap',
                  overflow: 'hidden',
                  alignItems: 'center',
                  height: '44px',
                  padding: '0'
                },
                '& .MuiAutocomplete-tag': {
                  margin: '1px 2px 1px 0',
                  height: '22px',
                  '& .MuiChip-root': {
                    height: '22px'
                  }
                }
              }
            }}
          />
        );
      }}
    />
  );
};
